import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { walletApi } from "../lib/api";
import { inr } from "../lib/format";
import { useApp } from "../context/AppContext";
import { GOLD } from "../components/WalletUI";

const POLL_MS = 3000;
const MAX_POLLS = 40; // ~2 minutes

const VIEW = {
  verifying: { icon: Loader2, spin: true, cls: "text-[#f5b400]", title: "Verifying your payment…", sub: "Please wait while we confirm with your bank." },
  success: { icon: CheckCircle2, cls: "text-emerald-400", title: "Payment successful", sub: "Your wallet has been credited." },
  pending: { icon: Clock, cls: "text-amber-400", title: "Payment still pending", sub: "If you completed the payment, your wallet will be credited automatically once the bank confirms it." },
  failure: { icon: XCircle, cls: "text-rose-400", title: "Payment not completed", sub: "No money was deducted for this order. You can try again." },
  expired: { icon: XCircle, cls: "text-rose-400", title: "Payment link expired", sub: "This order timed out. Please start a new deposit." },
  mismatch: { icon: Clock, cls: "text-amber-400", title: "Under review", sub: "The paid amount didn't match the order. Our team will reconcile it shortly." },
  notfound: { icon: XCircle, cls: "text-rose-400", title: "Order not found", sub: "We couldn't find this deposit on your account." },
};

export default function UpiReturn() {
  const [params] = useSearchParams();
  const clientTxnId = params.get("client_txn_id") || "";
  const { isAuthed, refresh } = useApp();
  const [dep, setDep] = useState(null);
  const [state, setState] = useState(clientTxnId ? "verifying" : "notfound");
  const polls = useRef(0);

  useEffect(() => {
    if (!isAuthed || !clientTxnId) return;
    let stop = false;
    let timer;
    const tick = async () => {
      try {
        const res = await walletApi.upiStatus(clientTxnId);
        if (stop) return;
        setDep(res.deposit);
        const s = res.deposit.status;
        if (s === "success") { setState("success"); refresh(); return; }
        if (s === "failure" || s === "expired" || s === "mismatch") { setState(s); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(tick, POLL_MS);
      } catch (e) {
        if (stop) return;
        if (e?.response?.status === 404) { setState("notfound"); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(tick, POLL_MS);
      }
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, clientTxnId]);

  const v = VIEW[state] || VIEW.verifying;
  const Icon = v.icon;

  return (
    <div className="max-w-md mx-auto px-4 py-10" data-testid="upi-return-page">
      <div className="rounded-3xl bg-[#0d1117] ring-1 ring-white/10 p-6 text-center shadow-[0_30px_60px_-30px_rgba(0,0,0,0.8)]">
        <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-white/[0.04] ring-1 ring-white/10">
          <Icon className={`h-9 w-9 ${v.cls} ${v.spin ? "animate-spin" : ""}`} />
        </div>
        <h1 className="mt-4 font-display font-bold text-2xl text-white" data-testid="upi-return-title">{v.title}</h1>
        <p className="mt-1.5 text-sm text-slate-400" data-testid="upi-return-sub">{v.sub}</p>

        {!isAuthed && (
          <p className="mt-4 text-sm text-amber-300" data-testid="upi-return-login-hint">Log in to see this deposit's status.</p>
        )}

        {dep && (
          <div className="mt-5 rounded-2xl bg-white/[0.03] ring-1 ring-white/10 divide-y divide-white/5 text-left text-[13px]" data-testid="upi-return-details">
            {[["Amount", inr(dep.amount)], ...(state === "success" && dep.bonus > 0 ? [["Bonus", `+ ${inr(dep.bonus)}`]] : []),
              ["Order", dep.client_txn_id], ...(dep.upi_txn_id ? [["UTR", dep.upi_txn_id]] : [])].map(([k, val]) => (
              <div key={k} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
                <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500">{k}</span>
                <span className="text-white font-semibold tabular-nums break-all text-right" data-testid={`upi-return-${k.toLowerCase()}`}>{val}</span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 grid gap-2">
          <Link to="/wallet?tab=deposit" data-testid="upi-return-wallet-btn"
            className={`inline-flex items-center justify-center gap-2 h-12 rounded-2xl font-extrabold text-black ${GOLD}`}>
            {state === "success" ? "Back to Wallet" : "Deposit again"} <ArrowRight className="h-5 w-5" />
          </Link>
          <Link to="/" data-testid="upi-return-home-btn" className="inline-flex items-center justify-center h-11 rounded-2xl text-sm font-semibold text-slate-300 ring-1 ring-white/10 hover:bg-white/5">
            Go to Home
          </Link>
        </div>
        <p className="mt-4 inline-flex items-center gap-1 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-[#f5b400]" /> Every payment is verified directly with the gateway before crediting.</p>
      </div>
    </div>
  );
}
